import { isSeqAcked, isSeqNewer } from '/shared/protocol.js';
import { sendInput } from '../network-client.js';
import {
  ACTIVE_INPUT_HEARTBEAT_MS,
  IDLE_INPUT_HEARTBEAT_MS,
  CONTROL_RESPONSIVE_WINDOW_MS,
  INPUT_RETRY_BASE_MS,
  INPUT_RETRY_MAX_ATTEMPTS,
  INPUT_RETRY_MAX_WINDOW_MS,
  INPUT_EVENT_DISPATCH_BACKDATE_MAX_MS,
  TURN_LEAD_PER_SPEED_MS,
  TURN_LEAD_MAX_MS,
  PU_TYPES,
  SFX
} from '../engine/config.js';
import { model, authoritative, ui, keyState, keyIntent, net } from '../engine/state.js';
import { settings } from '../ui/settings.js';
import {
  clamp,
  toSnapshotStruct
} from '../utils/utils.js';
import { syncServerClock, serverClockNowMs, serverNowMs } from '../engine/time.js';
import {
  playSound,
  scheduleStartSfx,
  clearScheduledStartSfx,
  flushScheduledStartSfx
} from '../audio/audio.js';
import {
  scoreSignature,
  overlayStateSignature,
  latestDeathSeq,
  powerupIdSet,
  alivePlayerCount
} from '../engine/state-helpers.js';
import { queueGemCollectionEffects } from '../render/render.js';
import { playerSpeedMultiplier } from '../render/effects.js';
import {
  normalizeInputTimestampMs,
  inputDispatchDelayMs
} from '../../core/input-timing.js';

function controlDirection(left, right) {
  if (left && !right) return -1;
  if (right && !left) return 1;
  return 0;
}

function inputLagReductionMs() {
  return clamp(Number(settings.inputLagReductionMs) || 0, 0, 50);
}

function normalizeInputEventClientMs(sourceEvent, fallbackMs = Date.now()) {
  return normalizeInputTimestampMs(
    sourceEvent?.timeStamp,
    fallbackMs,
    globalThis.performance?.timeOrigin,
    globalThis.performance?.now?.()
  );
}

function inputClientMs(sourceEvent, fallbackMs = Date.now()) {
  if (!settings.useKeyPressTimestamp || !sourceEvent) return fallbackMs;
  return normalizeInputEventClientMs(sourceEvent, fallbackMs);
}

// Ported from the CurveCrash steering overlay, which biases turns earlier in
// proportion to speed on the grounds that a fixed timing error costs
// proportionally more *distance* the faster you are moving.
//
// ASYMMETRY WARNING. Two properties of this game weaken the port, and both are
// worth re-checking before tuning TURN_LEAD_PER_SPEED_MS:
//   1. The speed powerup is binary here (2x, speedPowerupMaxStacks: 1), so this
//      is a flat 25 ms whenever SPEED is up -- not the overlay's dose-response
//      curve. Since the server clamps applyServerMs to the end of the last
//      simulated tick (20 ms), every value at or above one tick is equivalent.
//   2. Turn *starts* already subtract inputLagReductionMs (50 ms), which
//      saturates that clamp by itself, so this term cannot move them at all. It
//      only moves releases and reversals, which take the serverNowMs() branch.
// Net effect at 2x speed is therefore a turn that ends earlier without also
// starting earlier -- i.e. a SHORTER turn, so watch for under-steering rather
// than the intended lead. Set TURN_LEAD_PER_SPEED_MS to 0 to disable.
function speedAdaptiveTurnLeadMs() {
  if (!(TURN_LEAD_PER_SPEED_MS > 0)) return 0;
  const players = authoritative.state?.players;
  if (!Array.isArray(players)) return 0;
  const player = players.find((candidate) => candidate?.id === net.localPlayerId);
  if (!player) return 0;
  const multiplier = Number(playerSpeedMultiplier(player));
  if (!Number.isFinite(multiplier) || multiplier <= 1) return 0;
  return clamp(TURN_LEAD_PER_SPEED_MS * (multiplier - 1), 0, TURN_LEAD_MAX_MS);
}

function predictedApplyServerMs(sourceEvent = null, nowClientMs = Date.now(), backdate = true) {
  const eventClientMs = inputClientMs(sourceEvent, nowClientMs);
  const dispatchDelayMs = inputDispatchDelayMs(
    eventClientMs,
    nowClientMs,
    INPUT_EVENT_DISPATCH_BACKDATE_MAX_MS
  );
  const estimatedServerMs = backdate
    ? serverClockNowMs() - inputLagReductionMs()
    : serverNowMs();
  return estimatedServerMs - dispatchDelayMs - speedAdaptiveTurnLeadMs();
}

function resolveTurnKeys() {
  const leftDown = Boolean(keyIntent.leftDown);
  const rightDown = Boolean(keyIntent.rightDown);
  if (leftDown && !rightDown) return { left: true, right: false };
  if (rightDown && !leftDown) return { left: false, right: true };
  if (leftDown && rightDown) {
    if (keyIntent.lastPressedDir < 0) return { left: true, right: false };
    if (keyIntent.lastPressedDir > 0) return { left: false, right: true };
  }
  return { left: false, right: false };
}

function applyResolvedTurnKeys() {
  const resolved = resolveTurnKeys();
  const changed = keyState.left !== resolved.left || keyState.right !== resolved.right;
  keyState.left = resolved.left;
  keyState.right = resolved.right;
  return changed;
}

function controlRecentlyChanged(nowMs = Date.now()) {
  return net.lastControlChangeClientMs > 0
    && (nowMs - net.lastControlChangeClientMs) <= CONTROL_RESPONSIVE_WINDOW_MS;
}

function inputRetryIntervalMs() {
  const jitter = Number.isFinite(net.jitterMs) ? net.jitterMs : 0;
  const rtt = Number.isFinite(net.rttMs) ? net.rttMs : 0;
  const urgent = net.pendingInputs.length > 0 || controlRecentlyChanged();
  const rttScaled = rtt > 0 ? rtt * (urgent ? 0.22 : 0.3) : INPUT_RETRY_BASE_MS;
  const baseFloor = INPUT_RETRY_BASE_MS * (urgent ? 0.85 : 1);
  const jitterWeight = urgent ? 0.08 : 0.12;
  return clamp(
    Math.max(baseFloor, rttScaled) + jitter * jitterWeight,
    urgent ? 10 : 14,
    urgent ? 72 : 110
  );
}

function inputRetryDelayMs(retryCount = 0) {
  const base = inputRetryIntervalMs();
  const attempts = Math.max(0, Number(retryCount) || 0);
  return clamp(base * (1 + attempts * 0.2), base, 160);
}

function inputHeartbeatIntervalMs() {
  const nowMs = Date.now();
  const jitter = Number.isFinite(net.jitterMs) ? net.jitterMs : 0;
  const activeTurnInput = keyState.left !== keyState.right;
  const prioritizeLowLatency = activeTurnInput || controlRecentlyChanged(nowMs) || net.pendingInputs.length > 0;
  const base = prioritizeLowLatency ? ACTIVE_INPUT_HEARTBEAT_MS : IDLE_INPUT_HEARTBEAT_MS;
  const min = prioritizeLowLatency ? 28 : 72;
  const max = prioritizeLowLatency ? 68 : 132;
  const jitterWeight = prioritizeLowLatency ? 0.35 : 0.65;
  return clamp(base + jitter * jitterWeight, min, max);
}

function clearInputRetry(entry) {
  if (!entry) return;
  if (entry.retryTimer) {
    clearTimeout(entry.retryTimer);
    entry.retryTimer = 0;
  }
}

function clearAllInputRetries() {
  for (const entry of net.pendingInputs) {
    clearInputRetry(entry);
  }
}

function isInputSeqAcked(seq) {
  if (!Number.isFinite(Number(seq))) return false;
  if (!Number.isFinite(Number(net.lastAckSeq))) return false;
  return isSeqAcked(Number(seq) & 0xffff, Number(net.lastAckSeq) & 0xffff);
}

function hasNewerPendingInput(seq) {
  if (!Number.isFinite(Number(seq))) return false;
  const targetSeq = Number(seq) & 0xffff;
  for (const entry of net.pendingInputs) {
    if (!entry || !Number.isFinite(Number(entry.seq))) continue;
    const candidateSeq = Number(entry.seq) & 0xffff;
    if (candidateSeq === targetSeq) continue;
    if (isSeqNewer(candidateSeq, targetSeq)) return true;
  }
  return false;
}

function suppressSupersededInputRetries(latestSeq) {
  if (!Number.isFinite(Number(latestSeq))) return;
  const targetSeq = Number(latestSeq) & 0xffff;
  for (const entry of net.pendingInputs) {
    if (!entry || !Number.isFinite(Number(entry.seq))) continue;
    const entrySeq = Number(entry.seq) & 0xffff;
    if (entrySeq === targetSeq) continue;
    if (!isSeqNewer(targetSeq, entrySeq)) continue;
    entry.retrySuppressed = true;
    clearInputRetry(entry);
  }
}

function scheduleInputRetry(entry) {
  if (!entry || !Number.isFinite(Number(entry.seq))) return;
  if (entry.retrySuppressed) return;
  clearInputRetry(entry);

  const attempt = () => {
    if (!net.connected) return;
    if (isInputSeqAcked(entry.seq)) return;
    if (entry.retrySuppressed) return;
    if (hasNewerPendingInput(entry.seq)) return;
    if ((Date.now() - entry.inputClientMs) > INPUT_RETRY_MAX_WINDOW_MS) return;
    if (entry.retryCount >= INPUT_RETRY_MAX_ATTEMPTS) return;

    sendInput(entry.left, entry.right, {
      seq: entry.seq,
      clientTime: entry.inputClientMs,
      lastSnapTick: net.lastSnapshotTick,
      applyServerMs: entry.applyServerMs,
      reliableMirror: true
    });
    entry.retryCount += 1;
    entry.retryTimer = setTimeout(attempt, inputRetryDelayMs(entry.retryCount));
  };

  entry.retryTimer = setTimeout(attempt, inputRetryDelayMs(entry.retryCount));
}

function clearRoundPointBadgeVisuals() {
  for (const refs of ui.scoreRows.values()) {
    refs.roundDelta.hidden = true;
    refs.roundDelta.classList.remove('is-zero');
    refs.roundDelta.textContent = '';
  }
}

function removedGemPowerups(previousState, nextPowerupIds) {
  const previousPowerups = Array.isArray(previousState?.powerups) ? previousState.powerups : [];
  const removedGems = [];
  for (const powerup of previousPowerups) {
    if (powerup?.type !== PU_TYPES.GEM) continue;
    const id = Number(powerup?.id);
    if (!Number.isFinite(id) || nextPowerupIds.has(id)) continue;
    removedGems.push(powerup);
  }
  return removedGems;
}

function scoreGain(previousState, nextState) {
  const previousScores = Array.isArray(previousState?.scores) ? previousState.scores : [];
  const nextScores = Array.isArray(nextState?.scores) ? nextState.scores : [];
  const count = Math.max(previousScores.length, nextScores.length);
  let gained = 0;
  for (let i = 0; i < count; i += 1) {
    gained += Math.max(0, (Number(nextScores[i]) || 0) - (Number(previousScores[i]) || 0));
  }
  return gained;
}

function applyState(nextState) {
  if (!nextState || typeof nextState !== 'object') return;
  const prevState = (authoritative.state && typeof authoritative.state === 'object')
    ? authoritative.state
    : model.state;
  const prevStatus = prevState?.status || {};
  const prevDeathSeq = latestDeathSeq(prevState);
  const prevAliveCount = alivePlayerCount(prevState);
  const prevPowerupsVersion = ui.powerupsVersion;

  syncServerClock(nextState.serverNow);
  const mergedState = {
    ...prevState,
    ...nextState,
    status: {
      ...prevStatus,
      ...(nextState.status || {})
    }
  };
  authoritative.state = toSnapshotStruct(mergedState, prevState, { includeEvents: true });
  model.state = authoritative.state;

  const nextScoreSig = scoreSignature(authoritative.state);
  if (nextScoreSig !== ui.scoreSig) {
    ui.scoreSig = nextScoreSig;
    ui.scoreDirty = true;
  }

  const nextStatus = authoritative.state?.status || {};
  const nextOverlayStateSig = overlayStateSignature(authoritative.state);
  if (nextOverlayStateSig !== ui.overlayStateSig) {
    ui.overlayStateSig = nextOverlayStateSig;
    ui.overlayDirty = true;
  }
  // The sidebar's solo stop button appears and disappears with the match, and
  // updateSidebarControls only re-evaluates when something marks it dirty --
  // otherwise the button lingers after the match it would stop has ended.
  if (Boolean(nextStatus.running) !== Boolean(prevStatus.running)
    || Boolean(nextStatus.gameOver) !== Boolean(prevStatus.gameOver)) {
    ui.controlsDirty = true;
  }
  if (nextStatus.waitingNext || prevStatus.waitingNext) {
    ui.overlayDirty = true;
  }
  const startedNewRound = (
    nextStatus.running
    && !nextStatus.waitingNext
    && !nextStatus.gameOver
    && (prevStatus.waitingNext || prevStatus.gameOver)
  );
  if (startedNewRound) {
    clearRoundPointBadgeVisuals();
    ui.scoreDirty = true;
  }
  if ((nextStatus.waitingNext && !nextStatus.running) || nextStatus.gameOver) {
    clearScheduledStartSfx();
  }

  if (ui.hasStateSnapshot) {
    const nextDeathSeq = latestDeathSeq(authoritative.state);
    const nextAliveCount = alivePlayerCount(authoritative.state);
    const localNowMs = Date.now();
    const nextPowerupsVersion = Number(authoritative.state?.powerupsVersion);
    const shouldDiffPowerups = !Number.isFinite(nextPowerupsVersion)
      || nextPowerupsVersion !== prevPowerupsVersion;

    const newDeathCount = Math.max(0, nextDeathSeq - prevDeathSeq);
    const deathsByAliveDrop = Math.max(0, prevAliveCount - nextAliveCount);
    const deathBurst = Math.max(newDeathCount, deathsByAliveDrop);
    const hasNewDeaths = deathBurst > 0;

    const matchEnded = !prevStatus.gameOver && nextStatus.gameOver;
    if (matchEnded) {
      playSound(SFX.win, 0.5);
    } else {
      const roundEnded = !prevStatus.waitingNext && nextStatus.waitingNext && !nextStatus.gameOver;
      if (roundEnded && !hasNewDeaths) {
        playSound(SFX.roundEnd, 0.42);
      }
    }

    const prevFreeze = Number(prevStatus.roundStartFreezeUntilMs) || 0;
    const nextFreeze = Number(nextStatus.roundStartFreezeUntilMs) || 0;
    const roundStarted = (
      nextStatus.running
      && !nextStatus.gameOver
      && nextFreeze > prevFreeze + 50
    );
    if (roundStarted) {
      scheduleStartSfx(nextFreeze, nextState.serverNow, localNowMs);
      flushScheduledStartSfx(localNowMs);
    }

    if (hasNewDeaths) {
      playSound(SFX.death, 0.4);
    }

    if (shouldDiffPowerups) {
      const prevPowerupIds = ui.powerupIds;
      const nextPowerupIds = powerupIdSet(authoritative.state, ui.powerupIdsScratch);
      let appeared = 0;
      let removed = 0;
      for (const id of nextPowerupIds) {
        if (!prevPowerupIds.has(id)) appeared += 1;
      }
      for (const id of prevPowerupIds) {
        if (!nextPowerupIds.has(id)) removed += 1;
      }
      if (Number.isFinite(nextPowerupsVersion)) {
        ui.powerupsVersion = nextPowerupsVersion;
      }
      const swap = ui.powerupIds;
      ui.powerupIds = nextPowerupIds;
      ui.powerupIdsScratch = swap;

      if (appeared > 0 && settings.powerupSpawnSounds && nextStatus.running && !nextStatus.gameOver) {
        playSound(SFX.powerAppear, 0.28);
      }
      if (
        !roundStarted
        && nextStatus.running
        && !nextStatus.gameOver
        && removed > 0
        && scoreGain(prevState, authoritative.state) > 0
      ) {
        queueGemCollectionEffects(removedGemPowerups(prevState, nextPowerupIds), nextState.serverNow);
      }
      if (removed > 0 && removed <= 2 && !roundStarted && nextStatus.running && !nextStatus.gameOver) {
        playSound(SFX.powerGrab, 0.33);
      }
    }
  } else {
    const nextFreeze = Number(nextStatus.roundStartFreezeUntilMs) || 0;
    const sourceNow = Number(nextState.serverNow);
    if (
      nextStatus.running
      && !nextStatus.gameOver
      && nextFreeze > 0
      && Number.isFinite(sourceNow)
      && nextFreeze > sourceNow + 50
    ) {
      const localNowMs = Date.now();
      scheduleStartSfx(nextFreeze, sourceNow, localNowMs);
      flushScheduledStartSfx(localNowMs);
    }

    const nextPowerupsVersion = Number(authoritative.state?.powerupsVersion);
    const shouldSyncPowerups = !Number.isFinite(nextPowerupsVersion)
      || nextPowerupsVersion !== ui.powerupsVersion;
    if (shouldSyncPowerups) {
      const nextPowerupIds = powerupIdSet(authoritative.state, ui.powerupIdsScratch);
      if (Number.isFinite(nextPowerupsVersion)) {
        ui.powerupsVersion = nextPowerupsVersion;
      }
      const swap = ui.powerupIds;
      ui.powerupIds = nextPowerupIds;
      ui.powerupIdsScratch = swap;
    }
  }

  ui.hasStateSnapshot = true;
}

function applyAckSeq(rawAckSeq) {
  if (!Number.isFinite(Number(rawAckSeq))) return;
  const ackSeq = Number(rawAckSeq) & 0xffff;
  const hasLastAck = typeof net.lastAckSeq === 'number' && Number.isFinite(net.lastAckSeq);
  const lastAckSeq = hasLastAck ? (net.lastAckSeq & 0xffff) : 0;
  if (
    hasLastAck
    && ackSeq !== lastAckSeq
    && !isSeqNewer(ackSeq, lastAckSeq)
  ) {
    return;
  }
  net.lastAckSeq = ackSeq;
  dropAckedPendingInputs(ackSeq);
}

function dropAckedPendingInputs(ackSeq) {
  let latestAcked = null;
  for (const entry of net.predictionInputHistory) {
    if (entry && isSeqAcked(entry.seq, ackSeq)) {
      entry.acked = true;
    }
  }
  net.pendingInputs = net.pendingInputs.filter((entry) => {
    if (!isSeqAcked(entry.seq, ackSeq)) return true;
    clearInputRetry(entry);
    latestAcked = entry;
    return false;
  });
  if (latestAcked) {
    net.confirmedInput = {
      left: latestAcked.left,
      right: latestAcked.right,
      seq: latestAcked.seq
    };
  }
}

function sendCurrentInput(force = false, sourceEvent = null) {
  if (!net.connected) return;
  const nowMs = Date.now();
  const eventClientMs = inputClientMs(sourceEvent, nowMs);
  const left = Boolean(keyState.left);
  const right = Boolean(keyState.right);
  const previousDirection = controlDirection(
    Boolean(net.predictedInput?.left),
    Boolean(net.predictedInput?.right)
  );
  const nextDirection = controlDirection(left, right);
  const sig = `${left ? 1 : 0}:${right ? 1 : 0}`;
  const inputChanged = sig !== net.lastSentInputSig;
  const recentlyChanged = controlRecentlyChanged(nowMs);
  if (!inputChanged) {
    if (!force) return;
    if (nowMs - net.lastInputSendClientMs < inputHeartbeatIntervalMs()) return;
  }

  const clientMs = eventClientMs;
  // Backdate only the start of a turn for immediate visual response. Releases
  // and reversals stay on the current render timeline so replaying from the
  // latest snapshot preserves the path already shown to the player.
  const backdate = previousDirection === 0 && nextDirection !== 0;
  const rawApplyServerMs = predictedApplyServerMs(sourceEvent, nowMs, backdate);
  const lastPredictionEdge = net.predictionInputHistory[net.predictionInputHistory.length - 1];
  const lastPredictionApplyMs = Number(lastPredictionEdge?.applyServerMs);
  const applyServerMs = inputChanged && Number.isFinite(lastPredictionApplyMs)
    ? Math.max(rawApplyServerMs, lastPredictionApplyMs)
    : rawApplyServerMs;
  const seq = sendInput(left, right, {
    clientTime: clientMs,
    lastSnapTick: net.lastSnapshotTick,
    applyServerMs,
    reliableMirror: inputChanged || net.pendingInputs.length > 0 || recentlyChanged
  });
  net.lastSentInputSig = sig;
  if (inputChanged) {
    const entry = {
      seq,
      left,
      right,
      applyServerMs,
      inputClientMs: clientMs,
      retryCount: 0,
      retryTimer: 0,
      retrySuppressed: false,
      acked: false
    };
    net.pendingInputs.push(entry);
    net.predictionInputHistory.push(entry);
    net.debugStats.pendingInputHighWater = Math.max(
      net.debugStats.pendingInputHighWater,
      net.pendingInputs.length
    );
    net.debugStats.predictionHistoryHighWater = Math.max(
      net.debugStats.predictionHistoryHighWater,
      net.predictionInputHistory.length
    );
    if (net.predictionInputHistory.length > 128) {
      net.debugStats.predictionHistoryOverflows += 1;
    }
    net.predictedInput = { left, right };
    suppressSupersededInputRetries(seq);
    if (net.pendingInputs.length > 512) {
      const removeCount = net.pendingInputs.length - 512;
      for (let i = 0; i < removeCount; i++) {
        clearInputRetry(net.pendingInputs[i]);
      }
      net.pendingInputs.splice(0, removeCount);
    }
    if (net.predictionInputHistory.length > 1024) {
      net.predictionInputHistory.splice(0, net.predictionInputHistory.length - 1024);
    }
    scheduleInputRetry(entry);
  }
  net.lastInputSendClientMs = nowMs;
  if (inputChanged) {
    net.lastControlChangeClientMs = clientMs;
  }
}

export {
  applyResolvedTurnKeys,
  inputHeartbeatIntervalMs,
  clearAllInputRetries,
  applyState,
  applyAckSeq,
  sendCurrentInput
};
